import React from 'react';
import { ParquetFormState, DataType } from '../types';

interface OutputPreviewProps {
  form: ParquetFormState;
}

const SPOT_TYPE: DataType = 'SPOT OHLCV';

function OutputPreview({ form }: OutputPreviewProps) {
  const hasContent =
    form.coins.length > 0 && form.dataTypes.length > 0 && form.name.trim();

  if (!hasContent) return null;

  const zipName =
    form.dateFrom && form.dateTo
      ? `${form.name.trim().replace(/\s+/g, '_')}_${form.dataSource}_${form.dateFrom}_${form.dateTo}.zip`
      : `${form.name.trim().replace(/\s+/g, '_')}.zip`;

  const hasSpot = form.dataTypes.includes(SPOT_TYPE);
  const derivativeTypes = form.dataTypes.filter((dt) => dt !== SPOT_TYPE);
  const hasDerivatives = derivativeTypes.length > 0;

  const filesPerCoin = (hasSpot ? 1 : 0) + (hasDerivatives ? 1 : 0);
  const totalFiles = form.coins.length * filesPerCoin;

  const dateLabel =
    form.dateFrom && form.dateTo
      ? `${form.dateFrom} \u2192 ${form.dateTo}`
      : null;

  return (
    <div className="pq-preview">
      <label className="pq-label">Output Preview</label>
      <div className="pq-preview-box">
        {/* ZIP name */}
        <div className="pq-preview-filename">
          <span className="pq-tree-icon">{'\uD83D\uDCE6'}</span>
          <span className="pq-preview-name">{zipName}</span>
        </div>

        {/* File tree */}
        <div className="pq-tree">
          {form.coins.map((coin, coinIdx) => {
            const isLastCoin = coinIdx === form.coins.length - 1;
            const branch = isLastCoin
              ? '\u2514\u2500\u2500'
              : '\u251C\u2500\u2500';
            const indent = isLastCoin
              ? '\u00A0\u00A0\u00A0\u00A0'
              : '\u2502\u00A0\u00A0\u00A0';

            const files: { name: string; label: string }[] = [];
            if (hasSpot) {
              files.push({ name: 'spot_ohlcv.parquet', label: 'Spot OHLCV' });
            }
            if (hasDerivatives) {
              files.push({
                name: 'derivatives.parquet',
                label:
                  derivativeTypes.length === 1
                    ? derivativeTypes[0]
                    : `${derivativeTypes.length} types combined`,
              });
            }

            return (
              <React.Fragment key={coin}>
                <div className="pq-tree-line">
                  <span className="pq-tree-branch">{branch}</span>
                  <span className="pq-tree-icon">{'\uD83D\uDCC1'}</span>
                  <span className="pq-tree-folder">{coin}/</span>
                </div>
                {files.map((file, fileIdx) => {
                  const isLastFile = fileIdx === files.length - 1;
                  const fileBranch = isLastFile
                    ? '\u2514\u2500\u2500'
                    : '\u251C\u2500\u2500';
                  return (
                    <div className="pq-tree-line pq-tree-file" key={file.name}>
                      <span className="pq-tree-branch">
                        {indent}
                        {fileBranch}
                      </span>
                      <span className="pq-tree-icon">{'\uD83D\uDCC4'}</span>
                      <span className="pq-tree-filename">{file.name}</span>
                      <span className="pq-tree-hint">{file.label}</span>
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>

        {/* Summary tags */}
        <div className="pq-preview-meta">
          <span className="pq-preview-tag">
            {totalFiles} parquet file{totalFiles !== 1 ? 's' : ''}
          </span>
          <span className="pq-preview-tag">
            {form.coins.length} coin{form.coins.length !== 1 ? 's' : ''}
          </span>
          <span className="pq-preview-tag">
            {form.timeframes.length} timeframe
            {form.timeframes.length !== 1 ? 's' : ''}
          </span>
          {dateLabel && <span className="pq-preview-tag">{dateLabel}</span>}
        </div>
      </div>
    </div>
  );
}

export default OutputPreview;
